import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { Row } from 'react-table';
import { useFilterBarParams } from 'src/common/components/filter-bar/use-params';
import { DataTable } from 'src/common/components/table/data-table';
import { generateColumn } from 'src/common/components/table/LegacyTable';
import {
  TableSortableColumnHeader,
  TableSortableColumnHeaderOrderType,
  TableSortableColumnHeaderSortType,
} from 'src/common/components/table/sortable-column-header';
import { Pagination } from 'src/common/Pagination';
import { useCurrentOrganization } from 'src/store/organization';
import { Nullable } from 'src/types/nullable.type';
import { formatDuration } from 'src/utils/date';
import { apiGetAllJourneysInsights } from 'src/utils/journeyApi';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { useDepsMatchingCallbackRef } from 'src/utils/react/deps-matching-callback-ref.hook';
import { formatNumber } from 'src/utils/text';
import { JourneyMetrics } from 'src/dashboard/insights/constants';
import isNull from 'lodash/isNull';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import { useJourneys } from 'src/store/journeys';

export type AnalyticsJourneysListHeaderTypes =
  | 'name'
  | 'steps'

export type AnalyticsJourneysListHeaderSortType = TableSortableColumnHeaderSortType<AnalyticsJourneysListHeaderTypes>;

export const DashboardAnalyticsJourneysAll: FC<{}> = () => {
  const history = useHistory();
  const [journeysInsights, setJourneysInsights] = useState<any[]>([]);
  const [loading, setLoading] = useState<Nullable<boolean>>(null);
  const [pages, setPages] = useState(1);

  const { currentOrganization } = useCurrentOrganization();
  const { subscriptionPlanType, isOnTrial } = useCurrentSubscription();
  const { features, showUpgradeModal } = useFeatureRestrictions();
  const journeys = useJourneys((state) => state.journeys);

  const {
    filterBarParams: { search_term, page, sort_by, sort_order },
    updatePage,
    updateSort,
  } = useFilterBarParams();

  const onFetchJourneysInsightsRef = useDepsMatchingCallbackRef(
    (response: any) => {
      setJourneysInsights(response.insights);
      setPages(response.pages);
      setLoading(false);
    },
    [currentOrganization.id, search_term, page, sort_by, sort_order]
  );

  const fetchJourneysInsights = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiGetAllJourneysInsights(currentOrganization.id, {
        page,
        ...(search_term && { search_str: search_term }),
        ...(sort_by && { sort_by }),
        ...(sort_by && sort_order !== 'default' && { sort_order }),
      });
      onFetchJourneysInsightsRef.current &&
        onFetchJourneysInsightsRef.current(response, [currentOrganization.id, search_term, page, sort_by, sort_order]);
    } catch (e) {
      console.error(e);
    }
  }, [onFetchJourneysInsightsRef, currentOrganization.id, search_term, page, sort_by, sort_order]);

  useEffect(() => {
    fetchJourneysInsights();
  }, [fetchJourneysInsights]);

  const onRowClicked = (row: Row) => {
    const { journey } = row.original as any;
    
    // Check if journey is accessible
    if (!features.canAccessBasicInsights) {
      showUpgradeModal(
        'basic_insights',
        'You need access to basic insights to view journey analytics. Please contact support.',
        'Insights Access Required'
      );
      return;
    }
    
    history.push(`/${currentOrganization.slug}/analytics/journeys/${journey.uuid}`);
  };

  const onTableHeaderClick = (sortStack: AnalyticsJourneysListHeaderSortType) => {
    updateSort(sortStack.sortName, sortStack.orderBy);
  };

  const columns = useMemo(
    () => [
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'name'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='name'
            onClick={onTableHeaderClick}
            title='Name'
            tooltip='Journey name'
          />
        ),
        accessor: 'name',
        width: 462,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => (
          <span className='line-clamp-2 lg:line-clamp-none'>{row.original.journey.name}</span>
        ),
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'steps'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='steps'
            onClick={onTableHeaderClick}
            title='Steps'
            tooltip='Number of steps in Journey'
          />
        ),
        accessor: 'steps',
        width: 205.5,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return formatNumber(row.original.num_of_steps);
        },
      })
    ],
    [sort_by, sort_order]
  );

  const onPageChange = useCallback((e: any, newPage: number) => {
    updatePage(newPage);
  }, []);

  const renderContent = () => {
    if (loading || isNull(loading)) {
      return <FixedLoadingSpinner />;
    }

    if (journeysInsights.length > 0) {
      return <DataTable columns={columns} data={journeysInsights} onRowClick={onRowClicked} />;
    }

    if (search_term) {
      return <div className='text-bedrock-p text-bedrock-black'>No journeys found</div>;
    }

    return (
      <div className='max-w-[640px] text-bedrock-p text-bedrock-black'>
        Create some journeys to see their analytics here
      </div>
    );
  };

  return (
    <div className='flex flex-col'>
      <Helmet>
        <title>Journey | Analytics</title>
      </Helmet>
      {renderContent()}
      {pages > 1 && (
        <div className='mt-6'>
          <Pagination count={pages} page={page} onChange={onPageChange} />
        </div>
      )}
    </div>
  );
};
